import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";

// Icons
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WifiIcon from "@material-ui/icons/Wifi";

// Other Files
import { customTheme } from "../../constants/customTheme";
import classNames from "classnames";
import VirtualKeyboard from "../Shared/VirtualKeyboard";
import { colors } from "../../constants/colors";
import { CircularProgress } from "@material-ui/core";
// Actions
import {
  fetchConnectWifi,
  setVirtualKeyboard,
  fetchCheckWifi,
} from "../../actions";

const useStyles = makeStyles({
  root: (props) => customTheme(props.theme).dialog.root,
  buttonContainer: customTheme().dialog.buttonContainer,
  title: customTheme().dialog.title,
  contentContainer: (props) => ({
    ...customTheme().dialog.contentContainer,
    ...customTheme(props.theme).customScrollBar,
    overflowY: "auto",
    alignItems: "center",
    minHeight: "196px",
  }),
  contentItem: { ...customTheme().dialog.contentItem, minWidth: "300px" },
  contentItemCurrentStatus: { ...customTheme().dialog.contentItem },
  buttonOutlined: (props) => ({
    width: "110px",
    ...customTheme(props.theme).buttonOutlined,
  }),
  buttonExport: (props) => ({
    width: "170px",
    minHeight: "36.5px",
    ...customTheme(props.theme).buttonPrimary,
  }),
  buttonProgress: (props) => ({
    ...customTheme(props.theme).buttonProgress,
    color: colors[props.theme].primary,
  }),
  select: {
    marginRight: "0px",
  },
  titleIcon: {
    ...customTheme().titleIcon,
  },
  progressIndicator: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  menuScroll: (props) => ({
    "& .MuiMenu-paper": {
      ...customTheme(props.theme).customScrollBar,
    },
  }),
});

const WifiDialog = (props) => {
  const classes = useStyles(props);
  const [localState, setLocalState] = React.useState({
    selectedNetwork: "",
    networkPassword: "",
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: "networkPassword",
    inputUpdate: { networkPassword: "" },
  });

  const handleConnect = (wifi) => {
    props.setVirtualKeyboard(false);
    props.fetchConnectWifi(
      wifi,
      localState.selectedNetwork,
      localState.networkPassword
    );
  };

  const handleTurnOnWifi = () => {
    props.setVirtualKeyboard(false);
    props.fetchCheckWifi(true, true);
  };

  const handleTextFieldChange = () => (event) => {
    setLocalState({ ...localState, networkPassword: event.target.value });
    setKeyboardInput({
      ...keyboardInput,
      inputUpdate: { networkPassword: event.target.value },
    });
  };

  return (
    <Dialog
      open={props.open}
      onExiting={() => props.setVirtualKeyboard(false)}
      PaperProps={{ className: classNames(classes.root) }}
      disableBackdropClick={true}
    >
      <Typography
        className={classes.title}
        variant="h4"
        color="textPrimary"
        align="center"
      >
        <WifiIcon className={classes.titleIcon} />
        <span> Wifi Connection</span>
      </Typography>

      <DialogContent className={classes.contentContainer}>
        {props.fetchingCheckWifi || props.fetchingWifiConnection ? (
          <div className={classes.progressIndicator}>
            <Typography
              className={classes.contentItemCurrentStatus}
              color="textPrimary"
            >
              Status:{" "}
            </Typography>
            <CircularProgress size={20} />
          </div>
        ) : (
          <Typography className={classes.contentItem} color="textPrimary">
            Status:{" "}
            {props.wifiConnection === false
              ? "Not connected to wifi"
              : `Connected to ${props.wifiConnection}`}
          </Typography>
        )}
        {props.fetchingWifiConnection ||
        props.fetchingCheckWifi ? null : props.availableNetworks === false ? (
          <Typography>
            Wifi is turned off. Would you like to turn on wifi? If previously
            connected to a network, the system will autoconnect.
          </Typography>
        ) : (
          <React.Fragment>
            <FormControl className={classes.contentItem}>
              <InputLabel>Select Wifi Network</InputLabel>
              <Select
                required
                className={classes.select}
                onChange={(event) =>
                  setLocalState({
                    ...localState,
                    selectedNetwork: event.target.value,
                  })
                }
                value={localState.selectedNetwork}
                MenuProps={{ className: classNames(classes.menuScroll) }}
              >
                {props.availableNetworks.length === 0 ? (
                  <MenuItem key={"noNetwork"} disabled={true} value="">
                    {props.fetchingCheckWifi
                      ? "Scanning for networks..."
                      : "No network detected"}
                  </MenuItem>
                ) : (
                  props.availableNetworks.map((network) => {
                    return (
                      <MenuItem
                        key={props.availableNetworks.indexOf(network)}
                        value={network}
                      >
                        {network}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>

            {/* PASSWORD */}
            <TextField
              required
              className={classes.contentItem}
              label="Password"
              type={showPassword ? "text" : "password"}
              value={localState.networkPassword}
              onChange={handleTextFieldChange()}
              onFocus={() => {
                props.setVirtualKeyboard(true);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </React.Fragment>
        )}
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        <Button
          onClick={() => props.onClose()}
          className={classes.buttonOutlined}
          variant="outlined"
        >
          Cancel
        </Button>

        {props.wifiConnection !== false && (
          <Button
            onClick={() => handleConnect(false)}
            className={classes.buttonExport}
            variant="contained"
            color="primary"
            disabled={props.fetchingWifiConnection}
          >
            {props.fetchingWifiConnection && (
              <CircularProgress size={25} className={classes.buttonProgress} />
            )}
            Diconnect
          </Button>
        )}

        {props.availableNetworks === false ? (
          <Button
            onClick={() => handleTurnOnWifi()}
            className={classes.buttonExport}
            variant="contained"
            color="primary"
            disabled={props.fetchingCheckWifi}
          >
            {props.fetchingCheckWifi && (
              <CircularProgress size={25} className={classes.buttonProgress} />
            )}
            Turn On WiFi
          </Button>
        ) : (
          <Button
            onClick={() => handleConnect(true)}
            className={classes.buttonExport}
            variant="contained"
            color="primary"
            disabled={
              localState.selectedNetwork.length === 0 ||
              localState.networkPassword.length === 0 ||
              props.fetchingWifiConnection
            }
          >
            {props.fetchingWifiConnection && (
              <CircularProgress size={25} className={classes.buttonProgress} />
            )}
            Connect
          </Button>
        )}
      </DialogActions>

      <VirtualKeyboard
        anchor={"center"}
        keyboardInitialInput={{ networkPassword: localState.networkPassword }}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={(event) =>
          setLocalState({ ...localState, networkPassword: event })
        }
      />
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchConnectWifi,
      setVirtualKeyboard,
      fetchCheckWifi,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    availableNetworks: state.utility.wifi.availableNetworks,
    fetchingCheckWifi: state.utility.wifi.fetchingCheckWifi,
    wifiConnection: state.utility.wifi.wifiConnection,
    fetchingWifiConnection: state.utility.wifi.fetchingWifiConnection,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WifiDialog);
